import { formatDayKey, localDate, shiftDateKey, type ProfilePack } from "@teamna/shared";
import { useState } from "react";
import { Button, ChipRow, Field, InlineError, Panel, showToast } from "../../components/index.ts";
import { useCarryOverPackage, useExtendPackage } from "@teamna/web-client/hooks/use-owner.ts";

/**
 * Extend and Carry over on one package (#ai-feedback 2026-10-26, card #1). Owner and admin only.
 * Extend moves the last day of a package to a later day. Carry over moves the unused sessions of a
 * package to the package that waits for it; the old package then ends today, or on the day of the
 * last session it paid for. The views take plain props, so Storybook draws them; `ExtendPanel` and
 * `CarryOverPanel` connect them to the API. With more than one package, the panel asks which one.
 */

/** The longest extension at one time, as the API (`extendPackage`): one year after the last day. */
const MAX_EXTEND_DAYS = 365;

/** The package picker of a panel. Draws nothing when there is only one package. */
function PackPicker({ packs, selectedId, onSelect }: { packs: ProfilePack[]; selectedId: string; onSelect: (id: string) => void }) {
  return (
    <ChipRow
      options={packs.map((pack) => ({ id: pack.purchaseId, label: `${pack.packageName}, ends ${formatDayKey(pack.endsOn)}` }))}
      selectedId={selectedId}
      onSelect={onSelect}
      label="Package"
      wrap
    />
  );
}

export function ExtendPanelView({
  name,
  packs,
  pending = false,
  error = null,
  onSubmit,
  onClose,
}: {
  /** The participant, for the title. */
  name: string;
  /** The packages Extend can change (`canExtend`). The first one is selected. */
  packs: ProfilePack[];
  pending?: boolean;
  error?: unknown;
  onSubmit: (input: { purchaseId: string; endsOn: string }) => void;
  onClose: () => void;
}) {
  const [purchaseId, setPurchaseId] = useState(packs[0]?.purchaseId ?? "");
  const pack = packs.find((row) => row.purchaseId === purchaseId) ?? packs[0];
  const first = pack ? shiftDateKey(pack.endsOn, 1) : "";
  const last = pack ? shiftDateKey(pack.endsOn, MAX_EXTEND_DAYS) : "";
  const [endsOn, setEndsOn] = useState(pack ? shiftDateKey(pack.endsOn, 7) : "");
  const valid = Boolean(pack) && endsOn >= first && endsOn <= last;

  return (
    <Panel
      open
      onClose={onClose}
      title={`Extend the package of ${name}`}
      actions={
        <>
          <Button loading={pending} disabled={!valid} onClick={() => pack && onSubmit({ purchaseId: pack.purchaseId, endsOn })}>
            Extend
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <PackPicker
          packs={packs}
          selectedId={purchaseId}
          onSelect={(id) => {
            setPurchaseId(id);
            const next = packs.find((row) => row.purchaseId === id);
            if (next) setEndsOn(shiftDateKey(next.endsOn, 7));
          }}
        />
        <Field
          label="New last day"
          type="date"
          value={endsOn}
          min={first}
          max={last}
          error={
            !pack || valid
              ? undefined
              : endsOn < first
                ? `Pick a day after ${formatDayKey(pack.endsOn)}.`
                : "Pick a day not more than one year after the last day."
          }
          onChange={(event) => setEndsOn(event.target.value)}
        />
        {pack && valid ? (
          <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
            <span dir="auto">{pack.packageName}</span> ends on {formatDayKey(pack.endsOn)} now. It will end on {formatDayKey(endsOn)}. When no session of the
            next package is booked yet, it starts the day after.
          </p>
        ) : null}
        {error ? <InlineError error={error} /> : null}
      </div>
    </Panel>
  );
}

export function CarryOverPanelView({
  name,
  packs,
  today,
  pending = false,
  error = null,
  onSubmit,
  onClose,
}: {
  name: string;
  /** The packages Carry over can move sessions from (`canCarryOver`). The first one is selected. */
  packs: ProfilePack[];
  /** Today in the business timezone, `yyyy-MM-dd`. */
  today: string;
  pending?: boolean;
  error?: unknown;
  onSubmit: (input: { purchaseId: string; sessions: number }) => void;
  onClose: () => void;
}) {
  const [purchaseId, setPurchaseId] = useState(packs[0]?.purchaseId ?? "");
  const pack = packs.find((row) => row.purchaseId === purchaseId) ?? packs[0];
  const left = pack?.sessionsLeft ?? 0;
  const sessions = `${left} unused ${left === 1 ? "session" : "sessions"}`;

  return (
    <Panel
      open
      onClose={onClose}
      title={`Move the unused sessions of ${name}?`}
      actions={
        <>
          <Button loading={pending} disabled={!pack} onClick={() => pack && onSubmit({ purchaseId: pack.purchaseId, sessions: left })}>
            Move the sessions
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <PackPicker packs={packs} selectedId={purchaseId} onSelect={setPurchaseId} />
        {pack ? (
          <p className="rounded-card bg-accent-surface px-4 py-3 text-[15px] text-ink" aria-live="polite">
            The {sessions} of <span dir="auto">{pack.packageName}</span> go to the next package. <span dir="auto">{pack.packageName}</span> then ends on{" "}
            {formatDayKey(today)}, or on the day of its last booked session. When no session of the next package is booked yet, it starts the day after.
          </p>
        ) : null}
        {error ? <InlineError error={error} /> : null}
      </div>
    </Panel>
  );
}

export function ExtendPanel({
  businessId,
  participantId,
  name,
  packs,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  packs: ProfilePack[];
  onClose: () => void;
}) {
  const extend = useExtendPackage(businessId);
  return (
    <ExtendPanelView
      name={name}
      packs={packs}
      pending={extend.isPending}
      error={extend.error}
      onClose={onClose}
      onSubmit={(input) =>
        extend.mutate(
          { participantId, ...input },
          {
            onSuccess: () => {
              showToast(`Package of ${name} extended to ${formatDayKey(input.endsOn)}`);
              onClose();
            },
          },
        )
      }
    />
  );
}

export function CarryOverPanel({
  businessId,
  participantId,
  name,
  packs,
  timezone,
  onClose,
}: {
  businessId: string;
  participantId: string;
  name: string;
  packs: ProfilePack[];
  timezone: string;
  onClose: () => void;
}) {
  const carry = useCarryOverPackage(businessId);
  return (
    <CarryOverPanelView
      name={name}
      packs={packs}
      today={localDate(new Date(), timezone)}
      pending={carry.isPending}
      error={carry.error}
      onClose={onClose}
      onSubmit={(input) =>
        carry.mutate(
          { participantId, ...input },
          {
            onSuccess: () => {
              showToast(`Unused sessions of ${name} moved to the next package`);
              onClose();
            },
          },
        )
      }
    />
  );
}
